import React from "react";
import { motion } from "framer-motion";
import {
  TbBuildingCommunity,
  TbUsers,
  TbSchool,
  TbBook,
  TbUser,
  TbSparkles,
} from "react-icons/tb";
import styles from "./RoleFeatures.module.css";

/* ============================================================
   ROLE DATA   pulled VERBATIM from the provided Excel.
   No invented copy. Original order preserved per column.

   AUDIT-VERIFIED COUNTS (total 41):
     School    (HeadMaster column)    = 6
     Parent    (Parent column)        = 6
     Class Teacher                    = 12
     Subject Teacher                  = 7
     Student                          = 10
   ============================================================ */

// HeadMaster column   all 6 items (rendered as "School" module)
const SCHOOL_FEATURES = [
  "School Performance Overview",
  "Pass Rate Forecast",
  "Teacher Effectiveness Ranking",
  "Curriculum Progress (All Subjects)",
  "AI School Report Card",
  "Dropout Risk Prediction",
];

// Parent column   all 6 items
const PARENT_FEATURES = [
  "Academic Health Score (0–100)",
  "Performance vs Class Average",
  "Early Warning Alerts",
  "Remedial Actions Status",
  "Future Projection (Finals Score)",
  "Attendance Percentage",
];

// Class Teacher column   all 12 items
const CLASS_TEACHER_FEATURES = [
  "Class Performance Heatmap",
  "Focus Students List",
  "Students Needing Extra Help (Prediction)",
  "Daily Attendance Tracker",
  "Behavior & Engagement Log",
  "Student Birthday & Events Calendar",
  "Remedial Actions Tracker",
  "Class vs School Average Comparison",
  "Monthly Class Report Generator",
  "Curriculum Progress Tracker",
  "Class Strength Analysis by Subject",
  "Dropout Risk Prediction",
];

// Subject Teacher column   all 7 items
const SUBJECT_TEACHER_FEATURES = [
  "Assessment Insights (Post-Exam Analysis)",
  "Remedial Effectiveness Tracker",
  "Weak Students in My Subject",
  "Curriculum Progress Tracker",
  "Subject Engagement Analysis",
  "Teaching Methodology Effectiveness",
  "Performance Prediction (Next Exam)",
];

// Student column   all 10 items
const STUDENT_FEATURES = [
  "Performance Overview (Line Chart)",
  "Academic Health Score (0–100)",
  "Strengths & Weak Subject Badges",
  "Subject-Wise Score Prediction",
  "Study Goals & Progress Tracker",
  "Remedial Suggestions",
  "Attendance Rate Dashboard",
  "Engagement Score & Trend",
  "Exam Preparation Checklist",
  "Daily Study Planner",
];

/* ============================================================
   Animation
   ============================================================ */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.4, 0, 0.2, 1] },
  }),
};

/* ============================================================
   FeatureList   UNIFIED feature presentation across all cards.
   ============================================================ */
const FeatureList = ({ features }) => (
  <ul className={styles.featureList}>
    {features.map((feature) => (
      <li key={feature} className={styles.featureItem}>
        <span className={styles.featureDot} aria-hidden="true">
          <TbSparkles size={11} strokeWidth={2.5} />
        </span>
        <span className={styles.featureText}>{feature}</span>
      </li>
    ))}
  </ul>
);

/* ============================================================
   RoleCard   single shared card component.
   ============================================================ */
const RoleCard = ({
  index,
  spanClass,
  accentClass,
  icon: Icon,
  kicker,
  title,
  description,
  features,
}) => (
  <motion.article
    custom={index}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    variants={fadeUp}
    className={`${styles.card} ${accentClass} ${spanClass}`}
  >
    <div className={styles.cardHead}>
      <div className={styles.cardHeadLeft}>
        <div className={styles.iconWrap}>
          <Icon size={22} strokeWidth={2} />
        </div>
        <div className={styles.cardHeadText}>
          <p className={styles.kicker}>{kicker}</p>
          <h3 className="subtitle">{title}</h3>
        </div>
      </div>
      <span className={styles.countTag}>{features.length} Analytics</span>
    </div>

    <p className={styles.cardLede}>{description}</p>

    <FeatureList features={features} />
  </motion.article>
);

/* ============================================================
   RoleFeatures   bento layout with 5 role cards.
   Layout (12-col base):
     Row 1: School (8) + Parent (4)
     Row 2: Class Teacher (12, hero)
     Row 3: Subject Teacher (7) + Student (5)
   ============================================================ */
const RoleFeatures = () => {
  return (
    <section className={`page-container ${styles.section}`}>
      {/* Header */}
      {/* <header className={styles.header}>
        <p className={styles.eyebrow}>Built for everyone in the schoolsssssssssss</p>
        <h2 className={styles.title}>
          One platform. <span className="gradientText">Five perspectives.</span>
        </h2>
        <p className={styles.lede}>
          From the school's board-ready report to the student's tomorrow study
          plan   every role gets exactly the view they need, powered by the
          same intelligent core.
        </p>
      </header> */}

      {/* Bento grid */}
      <div className={styles.bento}>
        {/* 1   SCHOOL (HeadMaster column, 6 features) */}
        <RoleCard
          index={0}
          spanClass={styles.span8}
          accentClass={styles.accentOrange}
          icon={TbBuildingCommunity}
          kicker="For Schools"
          title="Institution-wide outcomes"
          description="Track pass rates, curriculum coverage, and dropout risk across the whole school   board-ready insights, generated automatically."
          features={SCHOOL_FEATURES}
        />

        {/* 2   PARENT (6 features) */}
        <RoleCard
          index={1}
          spanClass={styles.span4}
          accentClass={styles.accentPrimary}
          icon={TbUsers}
          kicker="For Parents"
          title="One number says it all"
          description="A clear health score and the next steps when something needs attention."
          features={PARENT_FEATURES}
        />

        {/* 3   CLASS TEACHER (12 features, hero) */}
        <RoleCard
          index={2}
          spanClass={styles.span12}
          accentClass={styles.accentCyan}
          icon={TbSchool}
          kicker="For Class Teachers"
          title="Every student visible at one glance"
          description="From the class heatmap to dropout predictions   see who needs you, act in one click, and never let a student fall through the cracks."
          features={CLASS_TEACHER_FEATURES}
        />

        {/* 4   SUBJECT TEACHER (7 features) */}
        <RoleCard
          index={3}
          spanClass={styles.span7}
          accentClass={styles.accentPink}
          icon={TbBook}
          kicker="For Subject Teachers"
          title="Teach the subject, we'll handle the analysis"
          description="Post-exam insights, methodology effectiveness, and weak-student tracking   focused on the subject you teach."
          features={SUBJECT_TEACHER_FEATURES}
        />

        {/* 5   STUDENT (10 features) */}
        <RoleCard
          index={4}
          spanClass={styles.span5}
          accentClass={styles.accentPurple}
          icon={TbUser}
          kicker="For Students"
          title="Know your next step"
          description="Where you stand, where you're heading, and exactly what to study tomorrow."
          features={STUDENT_FEATURES}
        />
      </div>
    </section>
  );
};

export default RoleFeatures;